import { useCallback, useEffect, useState } from "react";
import { firstOrDefault, remove, where } from "../utils/collection";
import { BwjEventArgs } from "../entities/bwj-event";
import { isEmptyString } from "../utils/string";

export type EventNotificationHandler = (args?:any) => void;

interface IEventNotificationRegistration {
    eventName: string;
    handler: EventNotificationHandler;
    subscriptionId: string;
}

class EventNotificationServiceClass {
    constructor() {
        this.registrations = [];
    }

    public subscribe(eventName:string, subscriptionId:string, handler:EventNotificationHandler, thisObj?:any): void {
        // no event name, subscription ID, or handler given is a no-no, but won't throw an error (at this time)
        if(isEmptyString(eventName) || isEmptyString(subscriptionId) || !handler) { return; }

        //bind handler if necessary
        if(!!thisObj) {
            handler = handler.bind(thisObj);
        }

        // don't subscribe more than once -- overwrite previous subscription if need be
        const existing = firstOrDefault(this.registrations, r => r.subscriptionId === subscriptionId && r.eventName === eventName);
        if(!!existing) { 
            existing.handler = handler;
            return;
        }

        //create registration
        this.registrations.push({
            eventName,
            handler,
            subscriptionId
        });
    }

    public unsubscribe(eventName:string, subscriptionId:string) {
        this.registrations = remove(this.registrations, r => r.subscriptionId === subscriptionId && r.eventName === eventName);
    }

    public safeDispatch(eventName: string, args: any) {
        const safeArgs = new BwjEventArgs(args);
        this.dispatch(eventName, safeArgs);
    }

    public dispatch(eventName:string, args?:any):void {
        // event name required
        if(!eventName) { return; }

        // obtain and fire handlers for event
        where(this.registrations, r => r.eventName === eventName)
                .map(h => h.handler(args));
    }

    private registrations: IEventNotificationRegistration[];
}

export const EventNotificationService = new EventNotificationServiceClass();

export function useEventNotification(eventHandler: (args?: any) => void, deps: React.DependencyList, event: string, subscriber: string) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handler = useCallback(eventHandler, deps);

    useEffect(() => {
        EventNotificationService.subscribe(event, subscriber, handler);
    
        return () => {
          EventNotificationService.unsubscribe(event, subscriber);
        };
      }, [subscriber, event, handler]);
}

export function useTransientStatefulEventNotification<T>(event: string, subscriber: string) {
    const [getter, setter] = useState<BwjEventArgs<T> | undefined>(undefined);
    const handler = useCallback((arg: BwjEventArgs<T>) => setter(arg), []);
    useEffect(() => {
        EventNotificationService.subscribe(event, subscriber, handler);

        return () => {
            EventNotificationService.unsubscribe(event, subscriber);
        };
    }, [subscriber, event, handler]);

    return getter;
}

export function useStatefulEventNotification<T>(event: string, subscriber: string, initialValue: T) {
    const [getter, setter] = useState(initialValue);
    const handler = useCallback((arg: T) => setter(arg), []);
    useEffect(() => {
        EventNotificationService.subscribe(event, subscriber, handler);

        return () => {
            EventNotificationService.unsubscribe(event, subscriber);
        };
    }, [subscriber, event, handler]);

    return getter;
}